/**
 * UiSectionsController — 정체성 섹션 12종 중 인터랙티브한 7개 섹션의 일괄 마운트.
 * 신설: 2026-05-28 (12 섹션 패키지의 일부).
 *
 * 대상:
 *  - [data-swiper="tg-spotlight"]         → Swiper fade + autoplay + pagination + nav
 *  - [data-swiper="equipment-coverflow"]  → Swiper coverflow
 *  - [data-swiper="timeline-milestones"]  → Swiper free-mode horizontal
 *  - [data-section="cp-roadmap"]          → GSAP ScrollTrigger (connector draw)
 *  - [data-section="scale-zoom"]     → vanilla click + GSAP ScrollTrigger pin (optional)
 *  - [data-section="floor-plan"]          → vanilla DOM (zone btn ↔ SVG zone hover)
 *  - [data-section="faculty-spotlight"]   → vanilla prev/next + GSAP fade
 *  - [data-section="awards-wall"]         → GSAP count-up + MarqueeWhisper (기존 컨트롤러 재사용)
 *
 * 모든 모션은 motionOK() / isImmersiveCapable() 가드를 통과해야 활성화.
 */

import Swiper from "swiper";
import {
    Autoplay,
    EffectCoverflow,
    EffectFade,
    FreeMode,
    Mousewheel,
    Navigation,
    Pagination,
} from "swiper/modules";

import { getGsap, getScrollTrigger } from "../global/GsapGlobals";
import { DUR, EASE, motionOK, isImmersiveCapable } from "../global/MotionTokens";
import { EventsCalendar } from "./EventsCalendar";

export class UiSectionsController {
    static init(): void {
        UiSectionsController.initTgSpotlight();
        UiSectionsController.initEquipmentCoverflow();
        UiSectionsController.initTimelineMilestones();
        UiSectionsController.initNewsCarousel();
        UiSectionsController.initCpRoadmap();
        UiSectionsController.initScaleZoom();
        UiSectionsController.initFloorPlan();
        EventsCalendar.init();
        UiSectionsController.initFacultySpotlight();
        UiSectionsController.initAwardsWall();
    }

    /**
     * template:components-init 등으로 재호출해도 안전한 Swiper 전용 재초기화.
     * 각 init 은 stage 의 uiSwiperBound 가드로 idempotent 하므로 중복 초기화되지 않는다.
     * (클릭 핸들러가 있는 비-Swiper 섹션은 재바인딩 위험이 있어 여기서 재호출하지 않는다.)
     */
    static reinitSwipers(): void {
        UiSectionsController.initNewsCarousel();
        UiSectionsController.initTgSpotlight();
        UiSectionsController.initEquipmentCoverflow();
        UiSectionsController.initTimelineMilestones();
    }

    // ──────────────────────────────────────────────────────────────────────
    // ④ — Home Latest News Carousel (cards × 4 + peek)
    // prev/next 가 stage 외부(footer)에 있어 root(.home-news-carousel) 로 올라가 쿼리.
    // ──────────────────────────────────────────────────────────────────────
    private static initNewsCarousel(): void {
        // 같은 정체성 섹션이 한 페이지에 2회 이상 배치돼도 각각 초기화(querySelectorAll).
        document.querySelectorAll<HTMLElement>('[data-swiper="home-news-carousel"]').forEach((stage) => {
            if (stage.dataset.uiSwiperBound === "true") return;
            stage.dataset.uiSwiperBound = "true";

            const root = stage.closest<HTMLElement>(".home-news-carousel") ?? stage;
            const prev = root.querySelector<HTMLElement>("[data-swiper-prev]");
            const next = root.querySelector<HTMLElement>("[data-swiper-next]");

            new Swiper(stage, {
                modules: [Navigation],
                slidesPerView: 4.2,
                spaceBetween: 16,
                grabCursor: true,
                observer: true,
                observeParents: true,
                resizeObserver: true,
                breakpoints: {
                    0:    { slidesPerView: 1.2, spaceBetween: 12 },
                    640:  { slidesPerView: 2.2, spaceBetween: 14 },
                    1024: { slidesPerView: 3.2, spaceBetween: 16 },
                    1280: { slidesPerView: 4.2, spaceBetween: 16 },
                },
                navigation: { prevEl: prev ?? undefined, nextEl: next ?? undefined },
                a11y: { enabled: true },
            });
        });
    }

    // ──────────────────────────────────────────────────────────────────────
    // A2 — TG Spotlight Swiper
    // ──────────────────────────────────────────────────────────────────────
    private static initTgSpotlight(): void {
        document.querySelectorAll<HTMLElement>('[data-swiper="tg-spotlight"]').forEach((stage) => {
            if (stage.dataset.uiSwiperBound === "true") return;
            stage.dataset.uiSwiperBound = "true";

            const prev = stage.querySelector<HTMLElement>("[data-swiper-prev]");
            const next = stage.querySelector<HTMLElement>("[data-swiper-next]");
            const pagi = stage.querySelector<HTMLElement>("[data-swiper-pagination]");

            new Swiper(stage, {
                modules: [Autoplay, EffectFade, Navigation, Pagination],
                slidesPerView: 1,
                effect: "fade",
                fadeEffect: { crossFade: true },
                speed: 600,
                observer: true,
                observeParents: true,
                resizeObserver: true,
                autoplay: motionOK() ? { delay: 6000, disableOnInteraction: true } : false,
                navigation: { prevEl: prev ?? undefined, nextEl: next ?? undefined },
                pagination: pagi
                    ? { el: pagi, type: "fraction" }
                    : undefined,
                a11y: { enabled: true },
            });
        });
    }

    // ──────────────────────────────────────────────────────────────────────
    // B2 — Equipment Coverflow Swiper
    // ──────────────────────────────────────────────────────────────────────
    private static initEquipmentCoverflow(): void {
        document.querySelectorAll<HTMLElement>('[data-swiper="equipment-coverflow"]').forEach((stage) => {
            if (stage.dataset.uiSwiperBound === "true") return;
            stage.dataset.uiSwiperBound = "true";

            new Swiper(stage, {
                modules: [EffectCoverflow, Mousewheel, Navigation],
                effect: "coverflow",
                centeredSlides: true,
                slidesPerView: "auto",
                grabCursor: true,
                loop: true,
                spaceBetween: 24,
                observer: true,
                observeParents: true,
                resizeObserver: true,
                mousewheel: isImmersiveCapable() ? { forceToAxis: true } : false,
                coverflowEffect: {
                    rotate: 24,
                    stretch: 0,
                    depth: 140,
                    modifier: 1,
                    slideShadows: false,
                },
            });
        });
    }

    // ──────────────────────────────────────────────────────────────────────
    // D1 — Timeline Milestones Swiper (free-mode horizontal)
    // ──────────────────────────────────────────────────────────────────────
    private static initTimelineMilestones(): void {
        document.querySelectorAll<HTMLElement>('[data-swiper="timeline-milestones"]').forEach((stage) => {
            if (stage.dataset.uiSwiperBound === "true") return;
            stage.dataset.uiSwiperBound = "true";

            new Swiper(stage, {
                modules: [FreeMode, Mousewheel, Navigation],
                slidesPerView: "auto",
                spaceBetween: 0,
                freeMode: { enabled: true, momentum: true, momentumRatio: 0.6 },
                observer: true,
                observeParents: true,
                resizeObserver: true,
                mousewheel: isImmersiveCapable() ? { forceToAxis: true } : false,
                grabCursor: true,
            });
        });
    }

    // ──────────────────────────────────────────────────────────────────────
    // A3 — CP Roadmap (ScrollTrigger connector draw)
    // ──────────────────────────────────────────────────────────────────────
    private static initCpRoadmap(): void {
        const root = document.querySelector<HTMLElement>('[data-section="cp-roadmap"]');
        if (!root) return;

        const connector = root.querySelector<HTMLElement>(".sec-cp-roadmap__connector");
        const nodes     = root.querySelectorAll<HTMLElement>(".sec-cp-roadmap__node");
        if (!motionOK() || (!connector && !nodes.length)) return;

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();

        if (connector) {
            gsap.fromTo(
                connector,
                { scaleX: 0, transformOrigin: "left center" },
                {
                    scaleX: 1,
                    duration: DUR.slower,
                    ease: EASE.outQuart,
                    scrollTrigger: { trigger: root, start: "top 75%" },
                },
            );
        }

        if (nodes.length) {
            gsap.fromTo(
                nodes,
                { y: 24, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: DUR.base,
                    ease: EASE.outExpo,
                    stagger: 0.12,
                    scrollTrigger: { trigger: root, start: "top 70%" },
                },
            );
        }

        ScrollTrigger.refresh();
    }

    // ──────────────────────────────────────────────────────────────────────
    // A4 — Multi-scale Zoom (click toggle + optional ScrollTrigger pin)
    // ──────────────────────────────────────────────────────────────────────
    private static initScaleZoom(): void {
        const root = document.querySelector<HTMLElement>('[data-section="scale-zoom"]');
        if (!root) return;

        const steps = Array.from(root.querySelectorAll<HTMLElement>(".sec-scale-zoom__step"));
        const panes = Array.from(root.querySelectorAll<HTMLElement>(".sec-scale-zoom__pane"));
        if (!steps.length || !panes.length) return;

        const activate = (n: number) => {
            steps.forEach((s) => {
                const on = Number(s.dataset.step) === n;
                s.classList.toggle("is-active", on);
                s.setAttribute("aria-selected", on ? "true" : "false");
            });
            panes.forEach((p) => {
                const on = Number(p.dataset.pane) === n;
                p.classList.toggle("is-active", on);
                p.setAttribute("aria-hidden", on ? "false" : "true");
            });
        };

        steps.forEach((s) => {
            s.addEventListener("click", () => activate(Number(s.dataset.step)));
        });

        // Optional ScrollTrigger pin — desktop + motion-ok 만
        if (isImmersiveCapable()) {
            const gsap = getGsap();
            const ScrollTrigger = getScrollTrigger();
            ScrollTrigger.create({
                trigger: root,
                start: "top 20%",
                end: `+=${panes.length * 240}`,
                pin: root.querySelector<HTMLElement>(".sec-scale-zoom__pin") ?? undefined,
                pinSpacing: true,
                scrub: 0.6,
                onUpdate: (self) => {
                    const idx = Math.min(panes.length, Math.max(1, Math.ceil(self.progress * panes.length)));
                    activate(idx);
                },
            });
            void gsap;
        }
    }

    // ──────────────────────────────────────────────────────────────────────
    // B3 — Lab Floor Plan (vanilla)
    // ──────────────────────────────────────────────────────────────────────
    private static initFloorPlan(): void {
        const root = document.querySelector<HTMLElement>('[data-section="floor-plan"]');
        if (!root) return;

        const zones = Array.from(root.querySelectorAll<SVGGElement>(".sec-floor-plan__zone"));
        const btns  = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-zone-btn]"));
        const details = root.querySelector<HTMLElement>("[data-floor-details]");
        if (!zones.length || !details) return;

        const eyebrow = details.querySelector<HTMLElement>(".sec-floor-plan__detail-eyebrow");
        const titleEl = details.querySelector<HTMLElement>(".sec-floor-plan__detail-title");
        const descEl  = details.querySelector<HTMLElement>(".sec-floor-plan__detail-desc");

        const activate = (zoneId: string) => {
            zones.forEach((z) => z.classList.toggle("is-active", z.dataset.zone === zoneId));
            btns.forEach((b)  => b.classList.toggle("is-active",  b.dataset.zoneBtn === zoneId));

            const src = btns.find((b) => b.dataset.zoneBtn === zoneId);
            if (src && eyebrow && titleEl && descEl) {
                eyebrow.textContent = src.dataset.category ?? "";
                titleEl.textContent = (src.dataset.label ?? "") + " · " + (src.dataset.area ?? "");
                descEl.textContent  = src.dataset.desc ?? "";
            }
        };

        zones.forEach((z) => {
            z.addEventListener("mouseenter", () => activate(z.dataset.zone ?? ""));
            z.addEventListener("click",       () => activate(z.dataset.zone ?? ""));
        });
        btns.forEach((b) => {
            b.addEventListener("click", () => activate(b.dataset.zoneBtn ?? ""));
        });
    }

    // ──────────────────────────────────────────────────────────────────────
    // C1 — Faculty Spotlight (prev/next + GSAP fade)
    // ──────────────────────────────────────────────────────────────────────
    private static initFacultySpotlight(): void {
        const root = document.querySelector<HTMLElement>('[data-section="faculty-spotlight"]');
        if (!root) return;

        const persons = Array.from(root.querySelectorAll<HTMLElement>(".sec-faculty-spotlight__person"));
        const counter = root.querySelector<HTMLElement>("[data-spotlight-counter]");
        const prev    = root.querySelector<HTMLElement>("[data-spotlight-prev]");
        const next    = root.querySelector<HTMLElement>("[data-spotlight-next]");
        if (persons.length < 2) return;

        let idx = 0;
        const total = persons.length;

        const show = (n: number) => {
            idx = (n + total) % total;
            persons.forEach((p, i) => {
                const on = i === idx;
                p.classList.toggle("is-active", on);
                p.setAttribute("aria-hidden", on ? "false" : "true");
            });
            if (counter) counter.textContent = `${idx + 1} / ${total}`;

            if (motionOK()) {
                const gsap = getGsap();
                const active = persons[idx];
                gsap.fromTo(
                    active.querySelector(".sec-faculty-spotlight__content"),
                    { y: 16, opacity: 0 },
                    { y: 0, opacity: 1, duration: DUR.base, ease: EASE.outExpo },
                );
            }
        };

        prev?.addEventListener("click", () => show(idx - 1));
        next?.addEventListener("click", () => show(idx + 1));
    }

    // ──────────────────────────────────────────────────────────────────────
    // D2 — Awards Wall (count-up + marquee speed override)
    // ──────────────────────────────────────────────────────────────────────
    private static initAwardsWall(): void {
        const root = document.querySelector<HTMLElement>('[data-section="awards-wall"]');
        if (!root) return;

        const stats = Array.from(root.querySelectorAll<HTMLElement>(".sec-awards-wall__stat-value"));
        if (!stats.length) return;

        if (!motionOK()) {
            stats.forEach((el) => {
                const to = Number(el.dataset.countTo || "0");
                el.textContent = `${to}${el.dataset.countSuffix ?? ""}`;
            });
            return;
        }

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();

        stats.forEach((el) => {
            const to = Number(el.dataset.countTo || "0");
            const suffix = el.dataset.countSuffix ?? "";
            const counter = { v: 0 };

            gsap.to(counter, {
                v: to,
                duration: DUR.epic,
                ease: EASE.outQuart,
                onUpdate: () => {
                    el.textContent = `${Math.round(counter.v)}${suffix}`;
                },
                scrollTrigger: { trigger: el, start: "top 80%", once: true },
            });
        });

        ScrollTrigger.refresh();
    }
}
